import React, { memo } from 'react';
import styled from 'styled-components';

import { formatMoneyCurrency } from 'components/pages/_detailShared/format';
import { StudentColors } from 'components/pages/StudentDetail/parts/studentColors';

export interface IDebtCellProps {
  /** Σ per-instalment remainder from the server (ТЗ 0029). */
  debtAmount: number | string | null;
  currency: string;
  terminated: boolean;
}

/** Coerce a possibly string/null money value to a finite number (0 fallback). */
const toNumber = (value: number | string | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Debt column cell for the Students_Table (Area 1, Req 1).
 *
 * Computes `Debt_Amount = max(0, budget − paid)`, coercing string decimals and
 * treating missing/non-numeric operands as `0` (Req 1.1, 1.7); a terminated row
 * forces debt to `0` (Req 1.4). Renders the amount via `formatMoneyCurrency`
 * with the row's own currency (Req 1.5), and shows a danger/warning dot when
 * `debt > 0` (Req 1.2–1.3) sourced from a `StudentColors` token (Req 1.6) — the
 * indicator is a styled-component, never an inline hex. The dot pairs color with
 * a shape so meaning is not conveyed by color alone (a11y).
 */
export const DebtCell = memo(
  ({ debtAmount, currency, terminated }: IDebtCellProps) => {
    // ТЗ 0029 — the server's Σ per-instalment remainder, not `budget − paid`.
    const debt = terminated ? 0 : toNumber(debtAmount);
    const hasDebt = debt > 0;

    return (
      <Wrap>
        {hasDebt ? <Dot aria-hidden="true" /> : null}
        <Amount hasDebt={hasDebt}>{formatMoneyCurrency(debt, currency)}</Amount>
      </Wrap>
    );
  },
);

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${StudentColors.danger};
`;

const Amount = styled.span<{ hasDebt: boolean }>`
  font-weight: 600;
  /* warnText, а не warn: сумма долга — главная цифра строки, а warn
     (#F3BB1C) даёт на белом 1.76:1 при норме 4.5. Точка рядом остаётся
     яркой — там цвет и должен быть сигнальным. */
  color: ${({ hasDebt }) =>
    hasDebt ? StudentColors.warnText : StudentColors.textMuted};
`;
